import moment from 'moment';
import React, { createContext, FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SquareImageComponent } from '../components';
import {
  AudienceCondition,
  AudienceFilterDateField,
  AudienceFilterNumberField,
  AudiencePixelStatus,
  useOrganizationsPageQuery,
} from '../types/api.graphql';
import { getOrganizationInitials } from '../utils/organization';
import { useCurrentOrgContext } from './CurrentOrgContext';

export interface ItemsType {
  text: string;
  id: string;
  key: string;
  disabled: boolean;
  icon: ReactNode;
  imgSrc?: string;
  description?: string;
}

export type FilterDropdownItem<T> = {
  text: string;
  id: T;
  icon?: ReactNode;
};

interface ListFilterContextProps {
  items: ItemsType[];
  setItems: React.Dispatch<React.SetStateAction<ItemsType[]>>;
  hasMore: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  page: number;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  loadingFirst: boolean;
  filter: ItemsType;
  setFilter: (value: ItemsType) => void;
  orgFilter: ItemsType | null;
  setOrgFilter: (value: ItemsType | null) => void;
  isCreationModalOpen: boolean;
  setIsCreationModalOpen: (value: boolean) => void;
  isSegmentModalOpen: boolean;
  setIsSegmentModalOpen: (value: boolean) => void;
  searchOrgInfiniteScroll: string;
  setSearchOrgInfiniteScroll: (value: string) => void;
  handleReset: (audienceCondition?: AudienceCondition) => void;
  audienceConditionFilter: FilterDropdownItem<AudienceCondition>;
  setAudienceConditionFilter: (value: FilterDropdownItem<AudienceCondition>) => void;
  pixelStatusFilter: FilterDropdownItem<AudienceFilterConditionType>;
  setPixelStatusFilter: (value: FilterDropdownItem<AudienceFilterConditionType>) => void;
  quotaUsageFilter: number;
  setQuotaUsageFilter: (value: number) => void;
  recentMatchDateFilter: string;
  setRecentMatchDateFilter: (value: string) => void;
}

interface ListFilterProviderProps {
  children: ReactNode;
}

const defaultValues: ListFilterContextProps = {
  items: null,
  setItems: null,
  hasMore: false,
  loadMore: null,
  loadingMore: false,
  page: null,
  searchTerm: null,
  setSearchTerm: null,
  loadingFirst: false,
  filter: null,
  setFilter: null,
  orgFilter: null,
  setOrgFilter: null,
  isCreationModalOpen: false,
  setIsCreationModalOpen: null,
  isSegmentModalOpen: false,
  setIsSegmentModalOpen: null,
  searchOrgInfiniteScroll: null,
  setSearchOrgInfiniteScroll: null,
  handleReset: null,
  audienceConditionFilter: null,
  setAudienceConditionFilter: null,
  pixelStatusFilter: null,
  setPixelStatusFilter: null,
  quotaUsageFilter: null,
  setQuotaUsageFilter: null,
  recentMatchDateFilter: null,
  setRecentMatchDateFilter: null,
};

const ListFilterContext = createContext<ListFilterContextProps>(defaultValues);

const PAGE_ITEMS_COUNT = 10;
export const QUOTA_USAGE_PERCENTAGE = 80;
export const WITHOUT_RECENT_MATCHES_DATE = moment().subtract(2, 'days').format('YYYY-MM-DD');
export type AudienceFilterConditionType = AudiencePixelStatus | AudienceFilterNumberField | AudienceFilterDateField;

export const ListFilterProvider: FC<ListFilterProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [items, setItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchOrgInfiniteScroll, setSearchOrgInfiniteScroll] = useState('');
  const [audienceConditionFilter, setAudienceConditionFilter] = useState<FilterDropdownItem<AudienceCondition>>(null);
  const [pixelStatusFilter, setPixelStatusFilter] = useState<FilterDropdownItem<AudienceFilterConditionType>>(null);
  const [quotaUsageFilter, setQuotaUsageFilter] = useState<number>(null);
  const [recentMatchDateFilter, setRecentMatchDateFilter] = useState<string>(null);

  const [filter, setFilter] = useState(null);
  const [orgFilter, setOrgFilter] = useState<ItemsType | null>(null);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [isSegmentModalOpen, setIsSegmentModalOpen] = useState(false);

  const { activeOrg } = useCurrentOrgContext();

  const buildActiveOrgItem = useCallback(
    (org: NonNullable<typeof activeOrg>): ItemsType => ({
      text: org.name,
      id: org.id,
      key: org.id,
      imgSrc: org.imageUrl,
      description: t(`organization.settings.orgTypes.${org.organizationType?.toLowerCase()}`),
      disabled: false,
      icon: <SquareImageComponent src={org.imageUrl} squared text={getOrganizationInitials(org.name || '')} />,
    }),
    [t],
  );

  // The global org selector (CurrentOrgContext) is the source of truth for which org
  // scopes Audiences/Dashboard queries. Keep `filter` in sync so the existing
  // `filter.id -> organizationId` query contract keeps working unchanged.
  useEffect(() => {
    setFilter(activeOrg ? buildActiveOrgItem(activeOrg) : undefined);
  }, [activeOrg, buildActiveOrgItem]);

  // A page-level org filter (e.g. on the Organizations page) is only valid within the current
  // global tree. Keep it in sync with the global org selector so the filter UI always reflects
  // the org actually scoping the results, whatever their organization type: a specific global org
  // shows as applied; "All Orgs" clears it. A previously selected descendant is reset when the
  // global org changes, since it may no longer belong to the new tree.
  // Depend on the id, not the object: `activeOrg` gets a new reference on every persistOrg call
  // (including resetActiveOrg), even when the id is unchanged, and that must not reset the filter.
  useEffect(() => {
    setOrgFilter(activeOrg ? buildActiveOrgItem(activeOrg) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrg?.id]);

  // `page` only starts at 1 on mount; after the first fetch it's bumped to 2 (see applyOrgsPage)
  // and the query below is skipped from then on (skip: page > 1) since ListFilterProvider never
  // remounts. Without resetting `page` here, a later activeOrg change would update the
  // `ancestorOrganizationId` variable but never actually refetch -- `items` would keep showing
  // whatever was fetched under the previous org tree, with only the pinned `filter` entry
  // (below) reflecting the new activeOrg. Must be declared before the data effect so React runs
  // it first in the same batch, ensuring this reset's setItems([]) executes before the data
  // effect's setItems([...nodes]).
  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [activeOrg?.id]);

  useEffect(() => {
    if (filter) {
      setItems((prevItems) => [filter, ...prevItems.filter((item) => item.id !== filter.id)]);
    }
  }, [filter]);

  // Must be declared before the data effect so React runs it first in the same batch,
  // ensuring the reset's setItems([]) executes before the data effect's setItems([...nodes]).
  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [searchOrgInfiniteScroll]);

  // fetchPolicy: 'network-only' ensures Apollo always returns a new object reference,
  // so useEffect([orgsQueryData]) reliably fires even when the cached data is identical.
  const {
    refetch,
    loading: loadingFirst,
    data: orgsQueryData,
  } = useOrganizationsPageQuery({
    variables: {
      pageNumber: page,
      pageItemsCount: PAGE_ITEMS_COUNT,
      searchQuery: searchOrgInfiniteScroll,
      ancestorOrganizationId: activeOrg?.id,
    },
    // Previously also skipped on the Organizations page (isOrganizationPage), back when nothing
    // there read `items`. OrganizationScopeFilter now renders in that page's header too and
    // depends on this list of org-tree items, so it must fetch there as well.
    skip: page > 1,
    fetchPolicy: 'network-only',
  });

  // Handles initial page-1 load. For load-more (page > 1), data is processed directly
  // in handleLoadMore from the refetch result, since Apollo v4 does not update the hook's
  // data value when skip=true even when refetch is called.
  const applyOrgsPage = useCallback(
    (dataOrgs: NonNullable<typeof orgsQueryData>) => {
      const newNodes = (
        dataOrgs?.organizationsPage?.nodes?.map((organization) => ({
          text: organization.name,
          imgSrc: organization.imageUrl,
          id: organization.id,
          key: organization.id,
          description: t(`organization.settings.orgTypes.${organization?.organizationType?.toLowerCase()}`),
          disabled: false,
          icon: (
            <SquareImageComponent
              src={organization.imageUrl}
              squared
              text={getOrganizationInitials(organization.name || '')}
            />
          ),
        })) || []
      ).filter((item) => item.id !== filter?.id);
      setItems((prevItems) => [
        // Always put the filtered org as the first item if exists
        ...(filter && !searchOrgInfiniteScroll ? [filter] : []),
        ...prevItems.filter((item) => item.id !== filter?.id),
        ...newNodes,
      ]);
      setHasMore(dataOrgs?.organizationsPage?.pageInfo?.hasNextPage);
      setPage((prevPage) => prevPage + 1);
    },
    [filter, searchOrgInfiniteScroll, t],
  );

  useEffect(() => {
    if (!orgsQueryData) return;
    applyOrgsPage(orgsQueryData);
    // Run once per Apollo data object. applyOrgsPage changes can re-append the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgsQueryData]);

  const handleLoadMore = () => {
    void (async () => {
      setLoadingMore(true);
      const result = await refetch({
        pageNumber: page,
        pageItemsCount: PAGE_ITEMS_COUNT,
        searchQuery: searchOrgInfiniteScroll,
        ancestorOrganizationId: activeOrg?.id,
      });
      if (result.data) applyOrgsPage(result.data);
      setLoadingMore(false);
    })();
  };

  const handleReset = (audienceCondition?: AudienceCondition) => {
    setItems([]);
    setPage(1);
    setFilter(undefined);
    if (audienceCondition === AudienceCondition.Pixel) {
      setPixelStatusFilter(null);
      setQuotaUsageFilter(null);
      setRecentMatchDateFilter(null);
    }
  };

  return (
    <ListFilterContext.Provider
      value={{
        items,
        setItems,
        hasMore,
        loadMore: handleLoadMore,
        loadingMore,
        page,
        searchTerm,
        setSearchTerm,
        loadingFirst,
        filter,
        setFilter,
        orgFilter,
        setOrgFilter,
        isCreationModalOpen,
        setIsCreationModalOpen,
        isSegmentModalOpen,
        setIsSegmentModalOpen,
        searchOrgInfiniteScroll,
        setSearchOrgInfiniteScroll,
        handleReset,
        audienceConditionFilter,
        setAudienceConditionFilter,
        pixelStatusFilter,
        setPixelStatusFilter,
        quotaUsageFilter,
        setQuotaUsageFilter,
        recentMatchDateFilter,
        setRecentMatchDateFilter,
      }}
    >
      {children}
    </ListFilterContext.Provider>
  );
};

export const useListFilterContext: () => ListFilterContextProps = () => {
  return React.useContext(ListFilterContext);
};
